import React, { useState, useContext, useCallback, useEffect, useMemo, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { AppContext, DispatchContext } from "../../AppContext";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import ClearIcon from "@material-ui/icons/Clear";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import TitleBar from "../../components/titleBar";
import Service from "../../services/service";
import CompareSource from "../../components/CompareSource";
import * as actions from "../../actions/actions";
import { useStyles } from "./styles";
import { normalizeSource, buildTextList } from "./utils";
import ProjectList from "./ProjectList";
import SimilarityHeatmap from "./SimilarityHeatmap";
import SimilarityChord from "./SimilarityChord";
import SimilarityTable from "./SimilarityTable";
import OptionsPanel from "./OptionsPanel";
import ComparisonEditPanel from "./ComparisonEditPanel";
import FileCard from "../../components/FileCard";
import FileUploadPanel from "../../components/FileUploadPanel";


// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function TextComparisons() {
    const state = useContext(AppContext);
    const dispatch = useContext(DispatchContext);
    const s = state.strings;
    const classes = useStyles();
    const tc = s.textComparisons || {};

    const { reportId: reportIdParam } = useParams();
    const history = useHistory();
    const initialReportId = useMemo(() => {
        const v = reportIdParam ? parseInt(reportIdParam, 10) : null;
        return isNaN(v) ? null : v;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [selectedId, setSelectedId] = useState(initialReportId);
    const setSelectedIdAndUrl = useCallback((id) => {
        setSelectedId(id);
        history.replace(id ? `/tools/comparisons/${id}` : "/tools/comparisons");
    }, [history]);
    const [projects, setProjects] = useState([]);
    const [selectedSources, setSelectedSources] = useState(null);
    const [loadingSources, setLoadingSources] = useState(false);
    const [hoveredCell, setHoveredCell] = useState(null);
    const [displayMode, setDisplayMode] = useState("heatmap");
    const [threshold, setThreshold] = useState([0, 100]);
    const [dynamicRibbonWidth, setDynamicRibbonWidth] = useState(true);
    const [showMatchesOnly, setShowMatchesOnly] = useState(true);
    const [separateMirroredView, setSeparateMirroredView] = useState(true);
    const [pendingLeft, setPendingLeft] = useState(null);
    const [collusionLibtexts, setCollusionLibtexts] = useState(null);
    const [openCompare, setOpenCompare] = useState(false);
    const [showUpload, setShowUpload] = useState(false);
    const [editingProject, setEditingProject] = useState(null); // { Id, Name, ProjectId, ProjectName }
    const editingProjectRef = useRef(null);
    const setEditingProjectAndRef = useCallback((val) => {
        const next = typeof val === "function" ? val(editingProjectRef.current) : val;
        editingProjectRef.current = next;
        setEditingProject(next);
    }, []);
    const [comparisonName, setComparisonName] = useState("");

    const selectedProject = projects.find((p) => p.Id === selectedId) || null;
    const selected = useMemo(
        () => selectedProject && selectedSources
            ? { id: selectedProject.Id, name: selectedProject.Name, sources: selectedSources }
            : null,
        [selectedProject, selectedSources]
    );
    const isGenerating = selectedProject?.Status === 'Aktiv';

    const loadComparisons = useCallback(() => {
        Service.getTextComparisons().then((res) => {
            if (res.success) {
                // Normalise to the shape ProjectList expects: {Id, Name, LibraryTexts}
                const mapped = res.data.map(c => ({
                    Id: c.ReportId,
                    Name: c.Name,
                    Status: c.Status,
                    LibraryTexts: c.TextCount,
                    MatchCount: c.MatchCount,
                    PendingCount: c.PendingCount,
                    ProjectId: c.ProjectId,
                    ProjectName: c.ProjectName,
                }));
                setProjects(mapped);
                // Keep editingProject in sync with fresh data
                const ep = editingProjectRef.current;
                if (ep) {
                    const fresh = mapped.find(p => p.Id === ep.Id);
                    if (fresh) setEditingProjectAndRef(fresh);
                }

            }
        });
    }, [setEditingProjectAndRef]);

    useEffect(() => {
        loadComparisons();
    }, [loadComparisons]);

    // Pick up libtexts queued from LibraryOverview
    useEffect(() => {
        if (state.pendingCollusionLibtexts && state.pendingCollusionLibtexts.length >= 2) {
            setCollusionLibtexts(state.pendingCollusionLibtexts);
            setSelectedIdAndUrl(null);
            dispatch(actions.setPendingCollusionLibtexts(null));
        }
    }, [state.pendingCollusionLibtexts, dispatch]);

    useEffect(() => {
        if (selectedId == null) { setSelectedSources(null); return; }
        setPendingLeft(null);
        setLoadingSources(true);
        setSelectedSources(null);
        // Always use reportId directly — GetTextComparisons uses ReportId as the item Id
        Service.getCollusionSources(null, selectedId, !showMatchesOnly).then((res) => {
            if (res.success) {
                setSelectedSources((res.data.sources || []).map(normalizeSource));
                setPendingLeft(res.data.left ?? null);
            }
            setLoadingSources(false);
        });
    }, [selectedId, showMatchesOnly]);

    useEffect(() => {
        if (!isGenerating || selectedId == null) return;
        const timer = setInterval(() => {
            Service.getCollusionSources(null, selectedId, !showMatchesOnly).then((res) => {
                if (!res.success) return;
                setPendingLeft(res.data.left ?? null);
                if ((res.data.left ?? 1) === 0) {
                    setSelectedSources((res.data.sources || []).map(normalizeSource));
                    loadComparisons();
                }
            });
        }, 5000);
        return () => clearInterval(timer);
    }, [selectedId, isGenerating, showMatchesOnly, loadComparisons]);

    const handleCellClick = useCallback(({ r, c }) => {
        if (!selectedSources) return;
        const texts = buildTextList(selectedSources);
        const t1 = texts[r];
        const t2 = texts[c];
        if (!t1 || !t2) return;
        const src = selectedSources.find(
            (s) => (s.text1.id === t1.id && s.text2.id === t2.id) ||
                   (s.text1.id === t2.id && s.text2.id === t1.id)
        );
        if (!src) return;
        dispatch(actions.setActiveSource({ Id: src.rawId, Title: src.title || `${t1.name} ↔ ${t2.name}` }));
        Service.getSourceMarkup(src.rawId).then((res) => {
            if (res.success && res.data) dispatch(actions.setSourceMarkup(res.data));
        });
        setOpenCompare(true);
    }, [selectedSources, dispatch]);

    const handleCloseCompare = useCallback(() => {
        setOpenCompare(false);
        dispatch(actions.setSourceMarkup({}));
    }, [dispatch]);

    const handleSourceClick = useCallback((src) => {
        if (!src || !src.rawId) return;
        dispatch(actions.setActiveSource({ Id: src.rawId, Title: src.title || `${src.text1.name} ↔ ${src.text2.name}` }));
        Service.getSourceMarkup(src.rawId).then((res) => {
            if (res.success && res.data) dispatch(actions.setSourceMarkup(res.data));
        });
        setOpenCompare(true);
    }, [dispatch]);

    const handleEditProject = useCallback((proj) => {
        if (proj.ProjectId) {
            dispatch(actions.openConfirmationDialog({
                title: tc.syncedComparisonTitle || "Synchronized Comparison",
                text: tc.syncedComparisonText ? tc.syncedComparisonText.format(proj.ProjectName) : `This text comparison is synchronized with the project "${proj.ProjectName}".`,
                hint: tc.syncedComparisonHint || "To change the texts in the comparison, add library texts to or remove them from the project.",
            }));
            return;
        }
        setEditingProjectAndRef(proj);
        setShowUpload(false);
        setCollusionLibtexts(null);
        setSelectedIdAndUrl(proj.Id);
    }, [dispatch, setSelectedIdAndUrl, setEditingProjectAndRef]);

    const handleDeleteProject = useCallback((proj) => {
        const isSynced = !!proj.ProjectId;
        if (isSynced) {
            dispatch(actions.openConfirmationDialog({
                title: s.projectsOverview.deactivateCollusionTitle,
                text: s.projectsOverview.deactivateCollusionText,
                hint: s.projectsOverview.deactivateCollusionHint,
                waitingMessage: s.projectsOverview.deactivateCollusionWaiting,
                onConfirm: () => {
                    Service.deleteCollusionReport(proj.ProjectId).then(res => {
                        dispatch(actions.closeConfirmationDialog());
                        if (res.success) {
                            if (selectedId === proj.Id) { setSelectedIdAndUrl(null); setSelectedSources(null); }
                            if (editingProject && editingProject.Id === proj.Id) setEditingProjectAndRef(null);
                            loadComparisons();
                            dispatch(actions.notifyProjectsChanged());
                        } else {
                            dispatch(actions.openToast({ type: "error", message: (res.errors || ["Error"]).join(", "), status: true }));
                        }
                    });
                },
            }));
        } else {
            dispatch(actions.openConfirmationDialog({
                title: tc.deleteComparisonTitle || "Delete Comparison",
                text: tc.deleteComparisonText ? tc.deleteComparisonText.format(proj.Name) : `Delete "${proj.Name}" and all its results?`,
                hint: tc.deleteComparisonHint || "Uploaded texts will be deleted. Library texts will not be affected.",
                okLabel: tc.deleteComparisonOk || "Delete",
                waitingMessage: tc.deleteComparisonWaiting || "Deleting…",
                onConfirm: () => {
                    Service.deleteTextComparison(proj.Id).then(res => {
                        dispatch(actions.closeConfirmationDialog());
                        if (res.success) {
                            if (selectedId === proj.Id) { setSelectedIdAndUrl(null); setSelectedSources(null); }
                            if (editingProject && editingProject.Id === proj.Id) setEditingProjectAndRef(null);
                            loadComparisons();
                        } else {
                            dispatch(actions.openToast({ type: "error", message: (res.errors || ["Error"]).join(", "), status: true }));
                        }
                    });
                },
            }));
        }
    }, [dispatch, s, selectedId, editingProject, loadComparisons, setEditingProjectAndRef]);

    return (
        <>
            {openCompare && <CompareSource noRating open={openCompare} onHandleClose={handleCloseCompare} />}
            <TitleBar title={tc.title || "Text Comparisons"} type="TextComparisons" onCreateNew={() => setShowUpload(true)} />
            <div className={classes.body}>
                <ProjectList
                    projects={projects}
                    selectedId={selectedId}
                    onSelect={(id) => { setShowUpload(false); setEditingProjectAndRef(null); setSelectedIdAndUrl(id); }}
                    onCreateNew={() => { setShowUpload(true); setEditingProjectAndRef(null); }}
                    onEdit={handleEditProject}
                    onDelete={handleDeleteProject}
                    strings={s}
                />
                <div className={classes.rightPanel}>
                    <div className={classes.heatmapPanel}>
                        <Paper className={classes.paper}>
                            {isGenerating && !showUpload && (
                                <div style={{ position: "absolute", inset: 0, zIndex: 10, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0,0,0,0.6)" }}>
                                    <CircularProgress style={{ color: "white", marginBottom: 16 }} size={70} />
                                    <div style={{ color: "white", textAlign: "center", fontSize: 16 }}>
                                        <div>{tc.generatingReport || "Collusion report is being generated"}</div>
                                        {pendingLeft !== null && (
                                            <div style={{ marginTop: 8, fontSize: 14 }}>{tc.pendingComparisons ? tc.pendingComparisons.format(pendingLeft) : `${pendingLeft} comparisons pending…`}</div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {collusionLibtexts ? (
                                <>
                                    <div className={classes.panelHeader}>
                                        {tc.newComparisonTitleN ? tc.newComparisonTitleN.format(collusionLibtexts.length) : `New Text Comparison – ${collusionLibtexts.length} Library Texts`}
                                    </div>
                                    <div style={{ padding: 16, flexShrink: 0 }}>
                                        <TextField
                                            label={tc.comparisonName || "Name of Text Comparison"}
                                            placeholder={tc.comparisonNamePlaceholder || "Optional"}
                                            value={comparisonName}
                                            onChange={(e) => setComparisonName(e.target.value)}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            InputProps={comparisonName ? {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" edge="end" onClick={() => setComparisonName("")}>
                                                            <ClearIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            } : undefined}
                                        />
                                    </div>
                                    <div style={{ width: "100%", minWidth: 0, minHeight: "100%", height: 0, overflow: "auto", display: "flex", flexWrap: "wrap", alignContent: "flex-start" }}>
                                        {collusionLibtexts.map((t) => (
                                            <div key={t.Id} style={{ width: "50%", padding: 8 }}>
                                                <FileCard
                                                    id={t.Id}
                                                    title={t.Name}
                                                    text1={((s.newCheck || {}).uploadFileWords || "").format
                                                        ? (s.newCheck.uploadFileWords).format(t.Words ?? 0, t.Pages ?? 0)
                                                        : `${t.Words ?? 0} words`}
                                                    projectId={t.ProjectId}
                                                    projectName={t.ProjectName}
                                                    type="txt"
                                                    progressValue={100}
                                                    onDelete={(e, id) => setCollusionLibtexts(prev => prev.filter(x => x.Id !== id))}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : showUpload ? (
                                <>
                                    <div className={classes.panelHeader}>
                                        {tc.newComparisonTitle || tc.newComparison || "New Text Comparison"}
                                    </div>
                                    <div style={{ padding: 16, flexShrink: 0 }}>
                                        <TextField
                                            label={tc.comparisonName || "Name of Text Comparison"}
                                            placeholder={tc.comparisonNamePlaceholder || "Optional"}
                                            value={comparisonName}
                                            onChange={(e) => setComparisonName(e.target.value)}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            InputProps={comparisonName ? {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" edge="end" onClick={() => setComparisonName("")}>
                                                            <ClearIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            } : undefined}
                                        />
                                    </div>
                                    <FileUploadPanel onCancel={() => setShowUpload(false)} showEdit={false} />
                                </>
                            ) : editingProject ? (
                                <>
                                    <div className={classes.panelHeader}>
                                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {tc.editPanelTitle ? tc.editPanelTitle.format(editingProject.Name) : `Edit – ${editingProject.Name}`}
                                        </span>
                                        <Tooltip title={tc.closeEdit || "Close"}>
                                            <IconButton size="small" style={{ marginLeft: 4, padding: 2 }} onClick={() => setEditingProjectAndRef(null)}>
                                                <CloseIcon style={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                    <ComparisonEditPanel
                                        reportId={editingProject.Id}
                                        projectId={editingProject.ProjectId}
                                        projectName={editingProject.ProjectName}
                                        reportName={editingProject.Name}
                                        onClose={() => setEditingProjectAndRef(null)}
                                        onNameChanged={(newName) => setEditingProjectAndRef(prev => prev ? { ...prev, Name: newName } : prev)}
                                        onChanged={() => {
                                            loadComparisons();
                                            if (selectedId === editingProject.Id) setSelectedSources(null);
                                        }}
                                    />
                                </>
                            ) : (
                                <>
                                    <div className={classes.panelHeader}>
                                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {selected
                                                ? `${tc.heatmapTitle || "Similarity Heatmap"} – ${selected.name}`
                                                : (tc.heatmapTitle || "Similarity Heatmap")}
                                        </span>
                                        {selected && selectedProject && (
                                            <Tooltip title={tc.editComparison || "Edit comparison"}>
                                                <IconButton size="small" style={{ marginLeft: 4, padding: 2 }} onClick={() => handleEditProject(selectedProject)}>
                                                    <EditIcon style={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </div>
                                    {selected && !isGenerating ? (
                                        selected.sources.length === 0 ? (
                                            <div className={classes.emptyHint}>
                                                <Typography variant="body2">
                                                    {tc.noSimilarities || "No similarities found."}
                                                </Typography>
                                            </div>
                                        ) : displayMode === "circular" ? (
                                            <SimilarityChord
                                                comparison={selected}
                                                onCellHover={setHoveredCell}
                                                onCellClick={handleCellClick}
                                                threshold={threshold}
                                                dynamicRibbonWidth={dynamicRibbonWidth}
                                            />
                                        ) : displayMode === "table" ? (
                                            <SimilarityTable
                                                comparison={selected}
                                                onCellHover={setHoveredCell}
                                                onSourceClick={handleSourceClick}
                                                threshold={threshold}
                                                strings={s}
                                            />
                                        ) : (
                                            <SimilarityHeatmap
                                                comparison={selected}
                                                onCellHover={setHoveredCell}
                                                onCellClick={handleCellClick}
                                                threshold={threshold}
                                                separateMirroredView={separateMirroredView}
                                            />
                                        )
                                    ) : !isGenerating && (
                                        <div className={classes.emptyHint}>
                                            <Typography variant="body2">
                                                {loadingSources
                                                    ? (tc.loading || "Loading…")
                                                    : (tc.noSelection || "Select a comparison on the left to view the similarity heatmap.")}
                                            </Typography>
                                        </div>
                                    )}
                                </>
                            )}
                        </Paper>
                    </div>
                    <OptionsPanel
                        hoveredCell={hoveredCell}
                        threshold={threshold}
                        onThresholdChange={setThreshold}
                        displayMode={displayMode}
                        onDisplayModeChange={setDisplayMode}
                        dynamicRibbonWidth={dynamicRibbonWidth}
                        onDynamicRibbonWidthChange={setDynamicRibbonWidth}
                        showMatchesOnly={showMatchesOnly}
                        onShowMatchesOnlyChange={setShowMatchesOnly}
                        separateMirroredView={separateMirroredView}
                        onSeparateMirroredViewChange={setSeparateMirroredView}
                        collusionLibtexts={collusionLibtexts}
                        showUpload={showUpload}
                        editingProject={editingProject}
                        onCompareLibtexts={(reportId) => {
                            setCollusionLibtexts(null);
                            setShowUpload(false);
                            setComparisonName("");
                            loadComparisons();
                            setSelectedIdAndUrl(reportId);
                        }}
                        selected={selected}
                        onCompare={(reportId) => {
                            setShowUpload(false);
                            setComparisonName("");
                            loadComparisons();
                            setSelectedIdAndUrl(reportId);
                        }}
                        comparisonName={comparisonName}
                    />
                </div>
            </div>
        </>
    );
}
